/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { Box, Heading, Stack, Text, Divider } from '@towersystems/towersystems-design-system';
import { connect } from 'react-redux';
import { map } from 'lodash';

/**
 * Internal dependencies
 */
import Main from 'components/main';
import Navigation from 'modules/dashboard/settings/navigation';
import AnnotatedSection from 'components/annotated-section';
import Card from 'components/card';
import { requestLocations, deleteLocation } from 'state/locations/actions';
import { getAllLocations } from 'state/locations/selectors';
import LocationSection from './location-section';
import Button from 'components/button';
import EditLocationModal from 'blocks/edit-location-modal';
import { startEditingLocation } from 'state/ui/location-modal/actions';
import AnnotatedSectionSkeleton from 'components/skeleton-loader/annotated-section-skeleton';
import cecSdk from 'lib/customer-ec-sdk';
import Notice from 'components/notice';
import { successNotice, errorNotice } from 'state/notices/actions';

/**
 * Style dependencies
 */
import './styles.scss';

const LocationsSettings = ({ locations, ...props }) => {
    const [requesting, setRequesting] = useState(true);
    const [error, setError] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setRequesting(true);
        try {
            await props.requestLocations();
        } catch (error) {
            setError(true);
        }
        setRequesting(false);
    };

    if (requesting) {
        return (
            <Main>
                <Stack space='large'>
                    <Navigation section='locations' />
                    <AnnotatedSectionSkeleton />
                </Stack>
            </Main>
        );
    }

    return (
        <Main>
            <Stack space='large'>
                <Navigation section='locations' />
                <Box paddingTop='xxlarge' paddingBottom='xxlarge'>
                    <Box display='flex' justifyContent='spaceBetween' alignItems='center' paddingBottom='xlarge'>
                        <Heading level='1'>Location Settings</Heading>
                    </Box>
                    <Divider />
                </Box>
                <Stack space='xlarge' dividers>
                    <AnnotatedSection>
                        <AnnotatedSection.Annotation
                            title='Locations'
                            description='Manage your locations. These should represent your locations in Retailer.'
                        />
                        <AnnotatedSection.Content>
                            <Stack space='large'>
                                <Card>
                                    {map(locations, (location) => {
                                        return (
                                            <LocationSection
                                                location={location}
                                                key={location.id}
                                                edit={props.startEditingLocation}
                                                deleteLocation={props.deleteLocation}
                                            />
                                        );
                                    })}
                                </Card>
                                <Card>
                                    <Card.Section>
                                        <Box display='flex' alignItems='center' justifyContent='spaceBetween'>
                                            <Text>Manually add location</Text>
                                            <Button onClick={() => props.startEditingLocation(null)} primary>
                                                Add Location
                                            </Button>
                                        </Box>
                                    </Card.Section>
                                </Card>
                            </Stack>
                        </AnnotatedSection.Content>
                    </AnnotatedSection>
                </Stack>
            </Stack>
            <EditLocationModal />
        </Main>
    );
};

export default connect(
    (state) => ({
        locations: getAllLocations(state),
    }),
    {
        requestLocations,
        startEditingLocation,
        deleteLocation,
        successNotice,
        errorNotice,
    },
)(LocationsSettings);
